/**
 * Каркас приложения: провайдеры, маршруты, общий layout с шапкой.
 *
 * Карточка дела — модалка поверх текущего экрана. Адрес всегда `/cases/:id`, поэтому
 * прямая ссылка снаружи работает: под модалкой отрисуется список дел. Когда карточку
 * открывают изнутри приложения (`useOpenCase`), в `location.state.backgroundLocation`
 * лежит экран, который остаётся под окном, — светофор, входящие или список.
 *
 * ВНИМАНИЕ. Экраны `DashboardPage`, `ExecutorDeskPage`, `CasesPage`, `CaseCardModal`,
 * `InboxPage`, `NewAppealPage`, `SettingsPage` пишут другие агенты. Пути файлов и имена
 * экспортов зафиксированы в `docs/spec/_web-contracts.md` и здесь не меняются.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
    BrowserRouter,
    Outlet,
    Route,
    Routes,
    matchPath,
    useLocation,
    useNavigate,
} from 'react-router-dom';

import { isApiError } from './api/client';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { RequireRole } from './auth/RequireRole';
import { ErrorState, LoadingState } from './components/ErrorState';
import { Header } from './components/Header';
import { useCloseCase } from './components/Modal';
import { ToastProvider } from './components/Toast';

import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import CaseCardModal from './components/case/CaseCardModal';
import CasesPage from './pages/CasesPage';
import DashboardChief from './pages/DashboardChief';
import DashboardExecutor from './pages/DashboardExecutor';
import InboxPage from './pages/InboxPage';
import NewAppealPage from './pages/NewAppealPage';
import SettingsPage from './pages/SettingsPage';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
                // Ошибки прав, валидации и «не найдено» повторять бессмысленно.
                if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
                return failureCount < 2;
            },
        },
        mutations: { retry: false },
    },
});

interface BackgroundState {
    backgroundLocation?: { pathname: string; search: string };
}

/** Главный экран зависит от роли: светофор руководителя либо рабочий стол исполнителя. */
function HomeScreen() {
    const { isChief, status } = useAuth();
    if (status === 'loading') return <LoadingState />;
    return isChief ? <DashboardChief /> : <DashboardExecutor />;
}

interface NavItem {
    to: string;
    label: string;
}

const NAV_CHIEF: NavItem[] = [
    { to: '/', label: '🚦 Светофор' },
    { to: '/cases', label: '📋 Дела' },
    { to: '/inbox', label: '📥 Входящие' },
    { to: '/settings', label: '⚙️ Настройки' },
];

const NAV_EXECUTOR: NavItem[] = [
    { to: '/', label: '🗂 Рабочий стол' },
    { to: '/cases', label: '📋 Дела' },
    { to: '/inbox', label: '📥 Входящие' },
];

/**
 * Верхние вкладки макета в роли главного меню. Кнопки, а не ссылки: CSS написан
 * под `.top-tabs button`, а новых имён классов мы не заводим.
 */
function MainNav() {
    const { isChief } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const items = isChief ? NAV_CHIEF : NAV_EXECUTOR;

    const isActive = (to: string) =>
        to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

    return (
        <div className="top-tabs" role="tablist">
            {items.map((item) => (
                <button
                    key={item.to}
                    type="button"
                    role="tab"
                    aria-selected={isActive(item.to)}
                    className={isActive(item.to) ? 'active' : undefined}
                    onClick={() => navigate(item.to)}
                >
                    {item.label}
                </button>
            ))}
        </div>
    );
}

function AppLayout() {
    return (
        <div className="app-container">
            <Header />
            <main className="app-content">
                <MainNav />
                <Outlet />
            </main>
        </div>
    );
}

function NotFoundScreen() {
    const navigate = useNavigate();
    return (
        <ErrorState
            title="Страница не найдена"
            message="Проверьте адрес или вернитесь на главный экран."
            onRetry={() => navigate('/', { replace: true })}
            retryLabel="На главную"
        />
    );
}

function AppRoutes() {
    const location = useLocation();
    const closeCase = useCloseCase();
    const { status } = useAuth();
    const background = (location.state as BackgroundState | null)?.backgroundLocation;
    const caseMatch = matchPath('/cases/:id', location.pathname);
    // Без сессии карточку не монтируем: RequireRole уже уводит на /login,
    // а запрос дела всё равно вернул бы 401.
    const caseId = status === 'authenticated' ? (caseMatch?.params.id ?? null) : null;

    return (
        <>
            <Routes location={background ?? location}>
                <Route path="/login" element={<LoginPage />} />
                <Route element={<RequireRole />}>
                    <Route element={<AppLayout />}>
                        <Route path="/" element={<HomeScreen />} />
                        <Route path="/cases" element={<CasesPage />} />
                        <Route path="/cases/:id" element={<CasesPage />} />
                        <Route path="/inbox" element={<InboxPage />} />
                        <Route path="/inbox/new" element={<NewAppealPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route
                            path="/settings"
                            element={
                                <RequireRole role="chief">
                                    <SettingsPage />
                                </RequireRole>
                            }
                        />
                        <Route path="*" element={<NotFoundScreen />} />
                    </Route>
                </Route>
            </Routes>

            {caseId && <CaseCardModal caseId={caseId} onClose={closeCase} />}
        </>
    );
}

export default function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AuthProvider>
                    <ToastProvider>
                        <AppRoutes />
                    </ToastProvider>
                </AuthProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}
